import React, { useEffect, useState } from 'react';
import DatahubAPI from 'api/DatahubAPI';
import SubscribeModal from 'components/SubscribeModal';
import { Redirect } from 'react-router-dom';
import { Typography } from '@mui/material';
import { Player } from '@lottiefiles/react-lottie-player';
import PaymentsAPI from 'api/PaymentsAPI';
import format from 'date-fns/format';
import CasesAPI from 'api/CasesAPI';
import { useSnackbar } from 'notistack';
import pako from 'pako';
import { Buffer } from 'buffer';
import TariffSearch from './TariffSearchComp';

interface ITariffProps {
  setShowFullRecord: any;
  cur_user: any;
  activeSub: any;
  userHasCommoditySearch: boolean;
  matchedTeamWithStripe: any;
  inputs: any;
  setInputs: any;
  page: number;
  setPage: any;
  screeningCus: any;
}

const Tariff: React.FC<ITariffProps> = ({ screeningCus, inputs, setInputs, page, setPage, activeSub, userHasCommoditySearch, matchedTeamWithStripe, cur_user, setShowFullRecord, }) => {
  const [ results, setResults ] = useState<any>({ isLoading: false, data: [] });

  const { enqueueSnackbar } = useSnackbar();
  const dt = new Date();
  const dtWithoutTz = new Date(dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000);
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');

  const [ saving, setSaving ] = useState(false);

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const handleTicket = async(values: any) => {
    const new_case = {
      groupId: cur_user.selected_team.id,
      ...values.basics,
      ...values,
    };
    const resp = await CasesAPI.createCase({ newCase: new_case });
    if (resp?.success) {
      setSaving(false);
      enqueueSnackbar('Successfully created new case', { variant: 'success' });
    } else if (resp) {
      const error_msg = 'Error saving new case. Uploaded file(s) may be too large.';
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
  };

  const compressData = (data: any) => {
    const stringifiedData = JSON.stringify(data);
    const compressed = pako.gzip(stringifiedData);
    return Buffer.from(compressed).toString('base64'); // Convert to base64 for storage or transmission
  };

  const createTicket = async(name: string) => {
    setSaving(true);
    const comFilterQuery = {
      lenses: [],
      countries: [],
      agencies: [],
      lists: [],
      hsCodeLists: [],
      sections: [],
      chapters: [],
      codes: [],
      entRes: false,
      filterDate: [],
    };

    const entFilterQuery = {
      lenses: [],
      countries: [],
      agencies: [],
      lists: [],
      entRes: false,
      filterDate: []
    };

    const ownerQuery = {
      kc_id: cur_user.basics?.id,
      email: cur_user.basics?.email,
      firstName: cur_user.basics?.firstName,
      lastName: cur_user.basics?.lastName,
    };

    const query: any = {
      ticketName: name,
      lastUpdate: formatted_dt,
      commodity: {
        harmonizedScheduleCode: inputs.hsCode,
        commodityName: inputs.desc,
        countryOfOrigin: inputs.origin,
        destinationCountry: inputs.destination
      },
      docType: 'Single Entry - Products',
      commodityFilter: comFilterQuery,
      entityFilter: entFilterQuery,
      frequency: 'Once',
      sharedWith: [],
      activeFilter: '',
      selectedOpts: [],
      status: 'Complete',
      favorite: false,
      groupName: cur_user.selected_team.name,
      template: [],
      colMapping: [],
      activity: {
        user: {
          kc_id: cur_user.basics?.id,
          email: cur_user.basics?.email,
          firstName: cur_user.basics?.firstName,
          lastName: cur_user.basics?.lastName,
        },
        action:  'created monitor ticket',
        timeStamp: Date.now()
      },
      owner: ownerQuery
    };
    const extractSources = (docs: any): any[] => {
      return docs.map((doc: any) => doc._source);
    };
    const length = results.data[0].result.body.hits.total.value;
    const structuredObj = [ {
      dataSet: 'Commodities',
      docs: extractSources(results.data[0].result.body.hits.hits),
      field: inputs.hsCode.length > 0 ? inputs.hsCode : inputs.desc,
      hits: length,
      row: 1
    } ];


    const ticketQuery = {
      ...query,
      curTotal: length,
      curNew: 0,
      results: {
        allResults: compressData(structuredObj),
        allTotal: length,
        newResults: [],
        newTotal: 0,
        date: formatted_dt,
        docInfo: []
      },
    };
    handleTicket(ticketQuery);
  };

  const triggerMeter = async(): Promise<any> => {
    const res = await PaymentsAPI.updateMeter(
      {
        type: 'screening_products_meter'
      },
      screeningCus.data.id
    );
    if (res?.success) {
      return res?.result;
    }
  };

  const handleSubmit = async(pageNum: number) => {
    setResults({ isLoading: true, data: [] });
    const allResults: any = [];
    const total = 0;
    const formatHTS = (input: string): string => {
      if (input.length === 0) {
        return '';
      }
      // Remove all non-digit characters
      const digitsOnly = input.replace(/\D/g, '');

      // Split into groups: 4, 2, 2, 2
      const parts = [];
      if (digitsOnly.length >= 4) {
        parts.push(digitsOnly.slice(0, 4));
      }
      if (digitsOnly.length >= 6) {
        parts.push(digitsOnly.slice(4, 6));
      }
      if (digitsOnly.length >= 8) {
        parts.push(digitsOnly.slice(6, 8));
      }
      if (digitsOnly.length === 10) {
        parts.push(digitsOnly.slice(8, 10));
      }

      return parts.join('.');
    };

    const val = formatHTS(inputs.hsCode);
    const query = {
      primary: val.length > 0 ? val : inputs.desc,
      indices: [ 'bite_app_commodities_*' ],
      agg: true,
      pageNum: pageNum
    };
    const res = await DatahubAPI.commoditySearch(query);
    if (res?.success) {
      allResults.push(res?.result);
      if (inputs.primary !== '') {
        triggerMeter();
      }
    }
    setResults({ isLoading: false, data: allResults });
  };

  const handleMultiPageChange = async(input: any, pageNum: number, i: number) => {
    const allResults: any = results.data;
    const query = {
      primary: input,
      indices: [ 'bite_app_commodities_*' ],
      agg: true,
      pageNum: pageNum
    };
    const res = await DatahubAPI.commoditySearch(query);
    if (res?.success) {
      allResults[i] = res?.result;
    }
    setResults({ isLoading: false, data: allResults });
  };

  useEffect(() => {
    setPage(0);
  }, [ inputs ]);

  return (
    <div className='screening-body-container' style={{ position: 'relative' }}>
      {/* Your existing content */}
      {activeSub === 'BITE Pro' || activeSub === 'BITE Enterprise' && !userHasCommoditySearch ? (
        <div className='no-license-modal-screening'>
          <Player
            autoplay
            loop
            src='https://lottie.host/3c23c1ad-7f71-4e3d-8f19-69154715dbdc/SdWWjCmU1J.json'
            style={{ width: '20%' }}
          />
          <Typography
            variant='h6'
            className='text-white'
            style={{
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.15px'
            }}
          >
            You need an assigned license to access this module
          </Typography>
          <Typography
            variant='h6'
            style={{
              fontSize: '8px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              marginTop: '20px',
              color: '#BDBEC1'
            }}
          >
            Visit Account &gt; Users &gt; to assign
            an available license to your user or contact your team admin
          </Typography>
        </div>
      ) : (
        !userHasCommoditySearch ? (
          <SubscribeModal
            name='Tariff Checker'
            screening={true}
            description='Check for tariffs that may apply to your products and receive the total calculation that you will need to account for in a transaction.'
            price={25}
            curUser={cur_user}
            stripeTeam={matchedTeamWithStripe}
            activeSub={activeSub}
          />
        ) : null
      )}
      <TariffSearch
        userHasSearch={userHasCommoditySearch}
        setShowFullRecord={setShowFullRecord}
        handleSubmit={handleSubmit}
        inputs={inputs}
        setInputs={setInputs}
        results={results}
        page={page}
        setPage={setPage}
        handleMultiPageChange={handleMultiPageChange}
        createTicket={createTicket}
        saving={saving}
      />

      {/* Blur overlay with Coming Soon */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backdropFilter: 'blur(3px)',
          backgroundColor: 'rgba(255, 255, 255, 0.3)', // faint white overlay
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2, // make sure it's above everything else
          borderRadius: '4px'
        }}
      >
        <Typography
          variant='h3'
          style={{
            color: '#333',
            fontWeight: 'bold',
            fontSize: '2rem'
          }}
        >
          Coming Soon!
        </Typography>
      </div>
    </div>
  );
};

export default Tariff;
